import React from 'react';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { ColorField, FormatBar, Group, Section, SelectField, Stepper } from '../controls';
import {
    FONT_FAMILIES, FONT_STYLES, FONT_WEIGHTS, TEXT_ALIGNS, TEXT_LINES, hasTextLine, toggleTextLine,
} from '../../TextBox/textStyle';
import styles from './sections.module.css';

const LABEL_ROWS = 2;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 72;

const ALIGN_OPTIONS = [
    { value: TEXT_ALIGNS.left, label: 'Align left', icon: <FormatAlignLeftIcon /> },
    { value: TEXT_ALIGNS.center, label: 'Align center', icon: <FormatAlignCenterIcon /> },
    { value: TEXT_ALIGNS.right, label: 'Align right', icon: <FormatAlignRightIcon /> },
];

// Italic, underline and strikethrough as on / off toggles over the node's fontStyle and textDecoration.
const formatToggles = ({ fontStyle, textDecoration }, change) => {
    const isItalic = fontStyle === FONT_STYLES.italic;
    const lineToggle = (line, label, icon) => ({
        label,
        icon,
        isOn: hasTextLine(textDecoration, line),
        onToggle: () => change({ textDecoration: toggleTextLine(textDecoration, line) }),
    });
    return [
        {
            label: 'Italic',
            icon: <FormatItalicIcon />,
            isOn: isItalic,
            onToggle: () => change({ fontStyle: isItalic ? FONT_STYLES.normal : FONT_STYLES.italic }),
        },
        lineToggle(TEXT_LINES.underline, 'Underline', <FormatUnderlinedIcon />),
        lineToggle(TEXT_LINES.strike, 'Strikethrough', <FormatStrikethroughIcon />),
    ];
};

/** The node's text: its label, font and size, weight and colour, then one bar of formatting and alignment (D-047). */
const TextSection = ({ values, change, mapColors }) => (
    <Section id="text" icon={<TextFieldsIcon />} title="Text">
        <Group label="Label">
            <textarea
                className={styles.labelInput}
                aria-label="Label"
                rows={LABEL_ROWS}
                placeholder="Add text"
                value={values.label}
                onChange={(event) => change({ label: event.target.value })}
            />
        </Group>
        <div className={styles.textGrid}>
            <Group label="Font">
                <SelectField label="Font" options={FONT_FAMILIES} value={values.fontFamily} onChange={(fontFamily) => change({ fontFamily })} />
            </Group>
            <Group label="Size">
                <Stepper
                    label="Font size"
                    min={MIN_FONT_SIZE}
                    max={MAX_FONT_SIZE}
                    value={values.fontSize}
                    onChange={(fontSize) => change({ fontSize })}
                />
            </Group>
            <Group label="Weight">
                <SelectField label="Font weight" options={FONT_WEIGHTS} value={values.fontWeight} onChange={(fontWeight) => change({ fontWeight })} />
            </Group>
            <Group label="Color">
                <ColorField label="Text color" color={values.textColor} mapColors={mapColors} onChange={(textColor) => change({ textColor })} />
            </Group>
        </div>
        <FormatBar
            toggles={formatToggles(values, change)}
            alignments={ALIGN_OPTIONS}
            alignment={values.textAlign}
            onAlign={(textAlign) => change({ textAlign })}
        />
    </Section>
);

export default TextSection;
